import { createRouter, createWebHistory } from 'vue-router'
import type { RouteRecordRaw } from 'vue-router'
import { useAuthStore } from '@/stores/auth'

// Lazy load components for better performance
const LoginPage = () => import('@/pages/LoginPage.vue')
const ForgotPasswordPage = () => import('@/pages/ForgotPasswordPage.vue')
const ResetPasswordPage = () => import('@/pages/ResetPasswordPage.vue')
const DashboardLayout = () => import('@/components/common/DashboardLayout.vue')
const DashboardPage = () => import('@/pages/DashboardPage.vue')
const UserManagementPage = () => import('@/pages/UserManagementPage.vue')
const AnalyticsPage = () => import('@/pages/AnalyticsPage.vue')
const MonetizationPage = () => import('@/pages/MonetizationPage.vue')
const FeedbackPage = () => import('@/pages/FeedbackPage.vue')
const DebugReportsPage = () => import('@/pages/DebugReportsPage.vue')
const SettingsPage = () => import('@/pages/SettingsPage.vue')

const routes: RouteRecordRaw[] = [
  {
    path: '/login',
    name: 'Login',
    component: LoginPage,
    meta: { requiresAuth: false, title: 'Login - Akuo Admin' }
  },
  {
    path: '/forgot-password',
    name: 'ForgotPassword',
    component: ForgotPasswordPage,
    meta: { requiresAuth: false, title: 'Forgot Password - Akuo Admin' }
  },
  {
    path: '/reset-password',
    name: 'ResetPassword',
    component: ResetPasswordPage,
    meta: { requiresAuth: false, title: 'Reset Password - Akuo Admin' }
  },
  {
    path: '/',
    component: DashboardLayout,
    meta: { requiresAuth: true },
    children: [
      {
        path: '',
        name: 'Dashboard',
        component: DashboardPage,
        meta: { title: 'Dashboard - Akuo Admin', icon: 'DataBoard' }
      },
      {
        path: '/users',
        name: 'UserManagement',
        component: UserManagementPage,
        meta: { title: 'User Management - Akuo Admin', icon: 'User' }
      },
      {
        path: '/analytics',
        name: 'Analytics',
        component: AnalyticsPage,
        meta: { title: 'Analytics - Akuo Admin', icon: 'TrendCharts' }
      },
      {
        path: '/monetization',
        name: 'Monetization',
        component: MonetizationPage,
        meta: { title: 'Monetization - Akuo Admin', icon: 'Money' }
      },
      {
        path: '/feedback',
        name: 'Feedback',
        component: FeedbackPage,
        meta: { title: 'Feedback - Akuo Admin', icon: 'ChatDotRound' }
      },
      {
        path: '/debug-reports',
        name: 'DebugReports',
        component: DebugReportsPage,
        meta: { title: 'Debug Reports - Akuo Admin', icon: 'Warning' }
      },
      {
        path: '/settings',
        name: 'Settings',
        component: SettingsPage,
        meta: { title: 'Settings - Akuo Admin', icon: 'Setting' }
      }
    ]
  },
  {
    path: '/:pathMatch(.*)*',
    redirect: '/'
  }
]

const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition
    } else {
      return { top: 0 }
    }
  }
})

// Navigation guards
router.beforeEach(async (to, from, next) => {
  const authStore = useAuthStore()
  
  // Set page title
  if (to.meta.title) {
    document.title = to.meta.title as string
  }

  // If going to login page, allow it without auth check
  if (to.name === 'Login') {
    // If already authenticated, redirect to dashboard
    if (authStore.isAuthenticated) {
      next('/')
      return
    }
    next()
    return
  }

  // Check if route requires authentication
  if (to.meta.requiresAuth !== false) {
    // Check if user is authenticated
    if (!authStore.isAuthenticated) {
      // Try to restore auth from stored token (only once per session)
      try {
        await authStore.initializeAuth()
      } catch (error) {
        console.log('Auth initialization failed:', error)
      }
      
      // If still not authenticated after initialization, redirect to login
      if (!authStore.isAuthenticated) {
        next('/login')
        return
      }
    }
  }

  next()
})

export default router
